import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CartService } from '../../../shared/services/cart.service';
import { WatchlistService } from '../../../shared/services/watchlist.service';
import { AuthService } from '../../../shared/services/auth.service';
import { StorageService } from '../../../shared/services/storage.service';
import { ShippingService } from '../../../shared/services/shipping.service';
import { ToastrService } from 'ngx-toastr';
import { hasPaidDownloadOptions } from '../../../shared/utils/download-options.util';
import { ProductComparisonService } from '../../../shared/services/product-comparison.service';

@Component({
  selector: 'app-product-preview-popup',
  standalone: false,
  templateUrl: './product-preview-popup.component.html',
  styleUrl: './product-preview-popup.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ProductPreviewPopupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() showModal: boolean = false;
  @Input() product: any;
  @Output() closeModalEmitter: EventEmitter<void> = new EventEmitter<void>();

  selectedGalleryImages: any[] = [];
  currentImageIndex: number = 0; // Add current image index
  priceHTTotal: number = 0;
  priceHTWithDiscount: number = 0;
  finalPriceTTC: number = 0;
  deliveryPrice: number = 0;
  protected variants: any = {};
  protected variantKeys: string[] = [];

  protected selectedVariants: any = {};
  protected selectedColor: any = null; // Add selected color tracking

  protected quantity = 1;
  priceWithTvaNoDiscount: number = 0;
  protected saleUnit = 1;

  // Add missing properties to match product details
  originalPriceTTC: number = 0;
  discountAmount: number = 0;

  // Specifications processing
  protected specifications: any[] = [];

  // Watchlist properties
  isInWatchlist = false;
  private subscription = new Subscription();

  // Loading state
  isLoadingFullDetails = false;
  private hasLoadedFullDetails = false;
  showDownloadModal = false;
  private pendingCartAdd = false;

  constructor(
    protected readonly cartService: CartService,
    private readonly watchlistService: WatchlistService,
    private readonly authService: AuthService,
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly cdr: ChangeDetectorRef,
    public readonly shippingService: ShippingService,
    private readonly comparisonService: ProductComparisonService
  ) { }

  /**
   * Checks if a product has a valid price
   */
  isValidProduct(product: any): boolean {
    return product && product.priceHT && product.priceHT > 0;
  }

  /**
   * Handles quotation requests for products without prices - redirects to product details
   */
  requestQuotation(product: any): void {
    // Close the modal first
    this.closeModal();

    // Navigate to product details page where user can request quotation
    if (product?.id) {
      this.router.navigate(['/products', product.id]);
    } else {
      this.toastr.error(
        'Nie udało się otworzyć szczegółów produktu.',
        'Błąd nawigacji',
        {
          timeOut: 3000,
          closeButton: true,
          progressBar: true
        }
      );
    }
  }

  ngOnInit(): void {
    this.checkWatchlistStatus();

    // Subscribe to watchlist changes
    this.subscription.add(
      this.watchlistService.watchlist$.subscribe(() => {
        this.checkWatchlistStatus();
      })
    );
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showModal']) {
      if (changes['showModal'].currentValue) {
        // Prevent body scroll when modal opens
        document.body.classList.add('modal-open');
      } else {
        // Restore body scroll when modal closes
        document.body.classList.remove('modal-open');
      }
    }

    if (changes['product'] && changes['product'].currentValue) {
      this.hasLoadedFullDetails = false;
      this.pendingCartAdd = false;
      this.showDownloadModal = false;
      // Always initialize with available data first for immediate feedback
      this.initializeProduct();

      // Check if we need to load full details
      // If product exists but might not have all variants loaded (common with search results/listings)
      if (this.product?.id) {
        // Force load full details to ensure we have all variants/technical data
        this.loadFullProductDetails(this.product.id);
      }
    }
  }
  calculateTotalPrice(): void {
    const priceHT = this.product?.priceHT ?? 0;
    const quantity = this.quantity ?? 0;
    this.deliveryPrice = this.shippingService.calculateItemShipping(
      { product: this.product, quantity: this.quantity },
      this.quantity
    );
    const tvaRate = this.product?.tvaRate ?? 0; // percent
    const discountRate = this.product?.discountRate ?? 0; // percent

    this.priceHTTotal = priceHT * quantity;

    // HT without discount + delivery
    const basePriceNoDiscount = this.priceHTTotal + this.deliveryPrice;

    // Original price TTC (without discount)
    // Note: VAT is calculated ONLY on the product price (priceHTTotal), not on delivery
    const tvaWithoutDiscount = (this.priceHTTotal * tvaRate) / 100;
    this.originalPriceTTC = this.priceHTTotal + tvaWithoutDiscount;
    this.priceWithTvaNoDiscount = this.originalPriceTTC;

    // Apply discount to the base price (before delivery and TVA)
    this.discountAmount = discountRate > 0 ? (this.priceHTTotal * discountRate) / 100 : 0;
    const discountedTotalHT = this.priceHTTotal - this.discountAmount;

    // HT with discount + delivery
    this.priceHTWithDiscount = discountedTotalHT + this.deliveryPrice;

    // Final price TTC with discount and TVA
    // Note: VAT is calculated ONLY on the product price (discountedTotalHT), not on delivery
    const tvaOnDiscountedPrice = (discountedTotalHT * tvaRate) / 100;
    this.finalPriceTTC = discountedTotalHT + tvaOnDiscountedPrice;
  }
  onSelectColor(color: any) {
    this.selectedColor = color;
    this.selectedGalleryImages = this.getSortedColorDocuments(color);
    this.currentImageIndex = 0; // Reset to first image when color changes
  }

  selectImage(index: number): void {
    this.currentImageIndex = index;
  }

  incrementQuantity(): void {
    this.quantity += (this.saleUnit || 1);
    this.calculateTotalPrice();
    this.cdr.markForCheck();
  }

  decrementQuantity(): void {
    const minQuantity = this.saleUnit || 1;
    const newQuantity = this.quantity - (this.saleUnit || 1);
    if (newQuantity >= minQuantity) {
      this.quantity = newQuantity;
      this.calculateTotalPrice();
      this.cdr.markForCheck();
    }
  }
  addToCart() {
    if (this.isLoadingFullDetails) {
      this.pendingCartAdd = true;
      return;
    }

    this.continueAddToCart();
  }

  private continueAddToCart(): void {
    if (!this.hasLoadedFullDetails) {
      if (this.product?.id) {
        this.pendingCartAdd = true;
        this.loadFullProductDetails(this.product.id);
      } else {
        this.toastr.error('Nie udało się załadować opcji produktu. Spróbuj ponownie.', 'Szczegóły produktu niedostępne');
      }
      return;
    }

    if (this.hasDownloadsWithPrice()) {
      this.showDownloadModal = true;
      return;
    }

    this.addProductToCart([]);
  }

  onDownloadSelectionConfirmed(selectedDownloads: any[]): void {
    this.showDownloadModal = false;
    this.addProductToCart(selectedDownloads);
  }

  closeDownloadModal(): void {
    this.showDownloadModal = false;
  }

  private hasDownloadsWithPrice(): boolean {
    return hasPaidDownloadOptions(this.product?.downloads);
  }

  private addProductToCart(selectedDownloads: any[]): void {
    // Calculate the correct discounted price per item
    const originalPriceHT = this.product?.priceHT || 0;
    const discountRate = this.product?.discountRate || 0;
    const discountedPriceHT = originalPriceHT * (1 - discountRate / 100);

    // Create deep copies of selected variants to avoid reference issues
    const selectedVariantsCopy: any = {};
    for (const key in this.selectedVariants) {
      if (this.selectedVariants.hasOwnProperty(key)) {
        selectedVariantsCopy[key] = {
          ...this.selectedVariants[key],
          // Ensure we have the essential properties for comparison
          id: this.selectedVariants[key]?.id,
          value: this.selectedVariants[key]?.value,
          variant: this.selectedVariants[key]?.variant ? {
            ...this.selectedVariants[key].variant
          } : null
        };
      }
    }

    // Create a unique ID for this specific cart item
    const cartItemId = new Date().getTime().toString() + '_' + Math.random().toString(36).substr(2, 9);

    this.cartService.addToCart({
      id: cartItemId,
      product: this.product,
      quantity: this.quantity,
      priceHT: discountedPriceHT, // Use discounted price instead of original price
      combination: null, // Preview doesn't have combinations
      variants: selectedVariantsCopy, // Use the deep copy with proper structure
      selectedColor: this.selectedColor ? { ...this.selectedColor } : null, // Deep copy color too
      selectedDownloads: selectedDownloads || [],
    });

    const downloadMessage = selectedDownloads.length > 0
      ? ` (dodatkowe materiały do pobrania: ${selectedDownloads.length})`
      : '';
    const toast = this.toastr.success(
      `${this.product?.label || 'Produkt'} dodano do koszyka${downloadMessage}`,
      'Zaktualizowano koszyk',
      {
        timeOut: 5000,
        closeButton: true,
        progressBar: true
      }
    );
    toast.onTap.subscribe(() => {
      this.cartService.openCartDrawer();
    });

    // Close the modal after adding to cart
    this.closeModal();
  }

  onSelectVariant(event: any) {
    this.selectVariant(event.key, event.variant);
  }

  selectVariant(key: any, value: any) {
    this.selectedVariants[key] = value;
    // this.selectVariantCombinations();
  }

  setVariants(variants: any) {
    this.variantKeys = [];
    if (!variants || !Array.isArray(variants)) {
      return;
    }

    variants.forEach((variant: any) => {
      if (variant?.variant?.id) {
        const variantId = variant.variant.id;

        if (!this.variants[variantId]) {
          this.variants[variantId] = {
            variant: variant.variant,
            values: [variant],
          };
          this.selectedVariants[variantId] = variant;
          this.variantKeys.push(variantId.toString());
        } else {
          this.variants[variantId].values.push(variant);
        }
      }
    });
  }
  objectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  shareProduct() {
    if (navigator.share && this.product) {
      // Use Web Share API if available
      navigator.share({
        title: this.product.label,
        text: `Zobacz ten produkt: ${this.product.label}`,
        url: `${window.location.origin}/products/${this.product.id}`
      }).catch((error) => { });
    } else if (this.product) {
      // Fallback: copy to clipboard
      const productUrl = `${window.location.origin}/products/${this.product.id}`;
      navigator.clipboard.writeText(productUrl).then(() => {
        this.toastr.success(
          'Link do produktu skopiowano do schowka.',
          'Udostępnij produkt',
          {
            timeOut: 2500,
            closeButton: true,
            progressBar: true
          }
        );
      }).catch((error) => {
        this.toastr.error(
          'Nie udało się skopiować linku do produktu.',
          'Błąd udostępniania',
          {
            timeOut: 2500,
            closeButton: true,
            progressBar: true
          }
        );
      });
    }
  }

  isInComparison(): boolean {
    return !!this.product?.id && this.comparisonService.contains(this.product.id);
  }

  toggleComparison(): void {
    if (!this.product?.id) {
      this.toastr.error('Nie znaleziono produktu.', 'Błąd');
      return;
    }

    if (this.isInComparison()) {
      this.comparisonService.remove(this.product.id);
      this.toastr.info('Produkt usunięto z porównania.', 'Zaktualizowano porównanie');
      return;
    }

    const result = this.comparisonService.add(this.product.id);
    if (result === 'added') {
      this.toastr.success('Produkt dodano do porównania.', 'Zaktualizowano porównanie');
    } else if (result === 'limit-reached') {
      this.toastr.warning('Możesz porównać maksymalnie cztery produkty. Usuń jeden, aby dodać kolejny.', 'Lista porównania jest pełna');
    } else if (result === 'already-added') {
      this.toastr.info('Ten produkt jest już na liście porównania.', 'Zaktualizowano porównanie');
    }
  }

  private checkWatchlistStatus(): void {
    if (!this.product?.id || !this.authService.isLoggedIn()) {
      this.isInWatchlist = false;
      return;
    }

    this.watchlistService.isInWatchlist(this.product.id).subscribe({
      next: (isInWatchlist) => {
        this.isInWatchlist = isInWatchlist;
      },
      error: (error) => {
        this.isInWatchlist = false;
      }
    });
  }

  isWatchlistPending(): boolean {
    return !!this.product?.id && this.watchlistService.isMutationPending(this.product.id);
  }

  toggleWatchlist(event: Event): void {
    event.stopPropagation();

    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/auth/login']);
      return;
    }

    if (!this.product?.id) {
      return;
    }
    if (this.watchlistService.isMutationPending(this.product.id)) return;

    if (this.isInWatchlist) {
      this.isInWatchlist = false;
      this.watchlistService.removeFromWatchlist(this.product.id).subscribe({
        next: () => {
          this.toastr.success(
            'Produkt usunięto z listy obserwowanych.',
            'Zaktualizowano listę obserwowanych',
            {
              timeOut: 2500,
              closeButton: true,
              progressBar: true
            }
          );
        },
        error: (error) => {
          this.isInWatchlist = this.watchlistService.containsProduct(this.product.id);
          this.toastr.error(
            error.message || 'Nie udało się usunąć produktu z listy obserwowanych.',
            'Błąd',
            {
              timeOut: 2500,
              closeButton: true,
              progressBar: true
            }
          );
        }
      });
    } else {
      this.isInWatchlist = true;
      this.watchlistService.addToWatchlist(this.product.id).subscribe({
        next: () => {
          this.toastr.success(
            'Produkt dodano do listy obserwowanych.',
            'Zaktualizowano listę obserwowanych',
            {
              timeOut: 2500,
              closeButton: true,
              progressBar: true
            }
          );
        },
        error: (error) => {
          this.isInWatchlist = this.watchlistService.containsProduct(this.product.id);
          this.toastr.error(
            error.message || 'Nie udało się dodać produktu do listy obserwowanych.',
            'Błąd',
            {
              timeOut: 2500,
              closeButton: true,
              progressBar: true
            }
          );
        }
      });
    }
  }

  closeModal() {
    // Restore body scroll
    document.body.classList.remove('modal-open');
    this.closeModalEmitter.emit();
  }

  /**
   * Determine the best unit label based on product context
   */
  private determineBestUnitLabel(): string {
    // Try to determine unit from product label or category
    const productLabel = (this.product?.label || '').toLowerCase();
    const categoryLabel = (this.product?.category?.label || '').toLowerCase();

    // Common measurement tool patterns
    if (productLabel.includes('caliper') || productLabel.includes('micrometer')) {
      return 'piece';
    }
    if (productLabel.includes('gauge') || productLabel.includes('indicator')) {
      return 'piece';
    }
    if (productLabel.includes('tester') || productLabel.includes('testing')) {
      return 'piece';
    }
    if (productLabel.includes('balance') || productLabel.includes('scale')) {
      return 'piece';
    }
    if (productLabel.includes('weight') && categoryLabel.includes('calibration')) {
      return 'set';
    }

    // Default fallback
    return 'unit';
  }

  /**
   * Check if a URL is a video file
   */
  isVideoFile(url: string): boolean {
    if (!url) return false;

    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv', '.wmv', '.m4v'];
    const lowerUrl = url.toLowerCase().split('?')[0];

    // Check for video extensions
    const hasVideoExtension = videoExtensions.some((ext: string) => lowerUrl.endsWith(ext));

    // Also check for common video indicators in URL
    const hasVideoIndicator = lowerUrl.includes('video') ||
      lowerUrl.includes('media') ||
      lowerUrl.includes('stream') ||
      lowerUrl.includes('play');

    return hasVideoExtension || hasVideoIndicator;
  }

  /**
   * Check if a URL is a YouTube video URL
   */
  isYouTubeUrl(url: string): boolean {
    if (!url) return false;
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    return youtubeRegex.test(url);
  }

  /**
   * Extract YouTube video ID from URL
   */
  getYouTubeVideoId(url: string): string | null {
    if (!url) return null;
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(youtubeRegex);
    return match ? match[1] : null;
  }

  /**
   * Parse specifications data similar to product details component
   */
  private parseSpecifications(): void {
    try {
      if (this.product?.specifications) {
        // Handle array (ideal case)
        if (Array.isArray(this.product.specifications)) {
          this.specifications = this.product.specifications;
          return;
        }

        // Handle string (common case from backend)
        if (typeof this.product.specifications === 'string') {
          try {
            const parsed = JSON.parse(this.product.specifications);
            // Check if it's an array after first parse
            if (Array.isArray(parsed)) {
              this.specifications = parsed;
              return;
            }
            // Handle double-encoded string
            if (typeof parsed === 'string') {
              const doubleParsed = JSON.parse(parsed);
              if (Array.isArray(doubleParsed)) {
                this.specifications = doubleParsed;
                return;
              }
            }
          } catch (e) {
            console.error('Failed to parse specifications:', e);
          }
        }
      }

      this.specifications = [];
    } catch (error) {
      console.error('Error parsing specifications:', error);
      this.specifications = [];
    }
  }

  /**
   * Get YouTube embed URL from video ID
   */
  getYouTubeEmbedUrl(url: string): string | null {
    const videoId = this.getYouTubeVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  }

  /**
   * Handle YouTube thumbnail loading errors
   */
  onYouTubeThumbnailError(event: any, originalUrl: string, index: number): void {
    const img = event.target as HTMLImageElement;
    const videoId = this.getYouTubeVideoId(originalUrl);

    if (videoId) {
      // Try medium quality thumbnail as fallback
      img.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      img.onerror = () => {
        // If medium quality also fails, try default quality
        img.src = `https://img.youtube.com/vi/${videoId}/default.jpg`;
        img.onerror = () => {
          // If all YouTube thumbnails fail, use a placeholder
          img.src = '/images/placeholder-product.svg';
        };
      };
    } else {
      // If no valid video ID, use placeholder
      img.src = '/images/placeholder-product.svg';
    }
  }

  /**
   * Toggle video playback (play/pause)
   */
  toggleVideoPlayback(video: HTMLVideoElement): void {
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }

  /**
   * Toggle video mute/unmute
   */
  toggleVideoMute(video: HTMLVideoElement): void {
    video.muted = !video.muted;
  }

  /**
   * Stop video and reset to beginning
   */
  stopVideo(video: HTMLVideoElement): void {
    video.pause();
    video.currentTime = 0;
  }

  ngOnDestroy(): void {
    // Unsubscribe from watchlist subscriptions
    this.subscription.unsubscribe();
    // Ensure body scroll is restored when component is destroyed
    document.body.classList.remove('modal-open');
  }

  /**
   * Get the first available image from product documents
   * Uses the same logic as admin and product details components
   */
  getFirstAvailableImage(): string | null {
    // Priority 0: Check if product has a thumbnail field from lightweight cache
    if (this.product?.thumbnail && typeof this.product.thumbnail === 'string' && this.product.thumbnail.trim() !== '') {
      return this.product.thumbnail;
    }

    // Priority 1: Check colors[0].documents first (same as product details)
    if (this.product?.colors && Array.isArray(this.product.colors) && this.product.colors.length > 0) {
      const firstColor = this.product.colors[0];
      if (firstColor?.documents && Array.isArray(firstColor.documents)) {
        // Sort color documents by their order field
        const sortedColorDocuments = [...firstColor.documents].sort((a: any, b: any) => {
          const orderA = a.order !== null && a.order !== undefined ? a.order : 999;
          const orderB = b.order !== null && b.order !== undefined ? b.order : 999;
          return orderA - orderB;
        });

        const firstValidColorDocument = sortedColorDocuments.find((doc: any) => {
          const isValid = doc && doc.url && doc.url.trim() !== '';
          const isImage = this.isImageUrl(doc?.url);
          return isValid && isImage;
        });

        if (firstValidColorDocument) {
          return firstValidColorDocument.url;
        }
      }
    }

    // Priority 2: Check main documents array (fallback)
    if (this.product?.documents && Array.isArray(this.product.documents)) {
      // Sort documents by their order field (same as product form)
      const sortedDocuments = [...this.product.documents].sort((a: any, b: any) => {
        const orderA = a.order !== null && a.order !== undefined ? a.order : 999;
        const orderB = b.order !== null && b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });

      // Try to find the best image by looking for specific patterns in labels
      const imageDocuments = sortedDocuments.filter((doc: any) => {
        const isValid = doc && doc.url && doc.url.trim() !== '';
        const isImage = this.isImageUrl(doc?.url);
        return isValid && isImage;
      });

      // Look for documents with specific labels that might indicate main product images
      const preferredLabels = ['main', 'primary', 'product', 'front', 'cover', 'hero'];
      const preferredDocument = imageDocuments.find((doc: any) =>
        preferredLabels.some(label =>
          doc?.label?.toLowerCase().includes(label)
        )
      );

      if (preferredDocument) {
        return preferredDocument.url;
      }

      // If no preferred document found, look for documents that don't have video-related labels
      const nonVideoDocument = imageDocuments.find((doc: any) => {
        const label = doc?.label?.toLowerCase() || '';
        const isVideoLabel = label.includes('video') || label.includes('youtube') || label.includes('demo');
        return !isVideoLabel;
      });

      if (nonVideoDocument) {
        return nonVideoDocument.url;
      }

      // Fallback to first valid image
      if (imageDocuments.length > 0) {
        return imageDocuments[0].url;
      }
    }

    // Priority 3: Check other colors if first color didn't have images
    if (this.product?.colors && Array.isArray(this.product.colors) && this.product.colors.length > 1) {
      for (let i = 1; i < this.product.colors.length; i++) {
        const color = this.product.colors[i];
        if (color?.documents && Array.isArray(color.documents)) {
          const sortedColorDocuments = [...color.documents].sort((a: any, b: any) => {
            const orderA = a.order !== null && a.order !== undefined ? a.order : 999;
            const orderB = b.order !== null && b.order !== undefined ? b.order : 999;
            return orderA - orderB;
          });

          const firstValidColorDocument = sortedColorDocuments.find((doc: any) => {
            const isValid = doc && doc.url && doc.url.trim() !== '';
            const isImage = this.isImageUrl(doc?.url);
            return isValid && isImage;
          });

          if (firstValidColorDocument) {
            return firstValidColorDocument.url;
          }
        }
      }
    }

    // Priority 4: Check other possible image fields
    if (this.product?.image && this.product.image.trim() !== '') {
      return this.product.image;
    }

    if (this.product?.images && Array.isArray(this.product.images) && this.product.images.length > 0) {
      const firstValidImage = this.product.images.find((img: any) => img && img.trim() !== '');
      if (firstValidImage) {
        return firstValidImage;
      }
    }

    return null;
  }

  /**
   * Get the first available image from a specific color
   */
  getFirstAvailableImageFromColor(color: any): string | null {
    if (color?.documents && Array.isArray(color.documents)) {
      // Sort color documents by their order field
      const sortedColorDocuments = [...color.documents].sort((a: any, b: any) => {
        const orderA = a.order !== null && a.order !== undefined ? a.order : 999;
        const orderB = b.order !== null && b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });

      const firstValidColorDocument = sortedColorDocuments.find((doc: any) => {
        const isValid = doc && doc.url && doc.url.trim() !== '';
        const isImage = this.isImageUrl(doc?.url);
        return isValid && isImage;
      });

      if (firstValidColorDocument) {
        return firstValidColorDocument.url;
      }
    }

    return null;
  }

  /**
   * Check if a URL is an image
   */
  private isImageUrl(url: string): boolean {
    if (!url) return false;

    const lowerUrl = url.toLowerCase().split('?')[0];
    const isVideoUrl = lowerUrl.includes('youtu.be') ||
      lowerUrl.includes('youtube.com') ||
      lowerUrl.includes('img.youtube.com') ||  // Exclude YouTube thumbnails
      lowerUrl.includes('vimeo.com') ||
      lowerUrl.endsWith('.mp4') ||
      lowerUrl.endsWith('.mov') ||
      lowerUrl.endsWith('.avi');

    if (isVideoUrl) return false;

    // Common image file extensions
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.tiff', '.ico'];

    // Check file extension
    const hasImageExtension = imageExtensions.some(ext =>
      lowerUrl.endsWith(ext.toLowerCase())
    );

    // Check for common image hosting services
    const isImageHosting = url.includes('imagekit.io') ||
      url.includes('cloudinary.com') ||
      url.includes('imgur.com') ||
      url.includes('amazonaws.com') ||
      url.includes('googleapis.com');

    // Check for data URLs (base64 images)
    const isDataUrl = url.startsWith('data:image/');

    return hasImageExtension || isImageHosting || isDataUrl;
  }

  /**
   * Initialize gallery images using smart image selection
   */
  private initializeGalleryImages(): void {
    // Priority 1: Check colors[0].documents first
    if (this.product?.colors && Array.isArray(this.product.colors) && this.product.colors.length > 0) {
      this.selectedGalleryImages = this.getSortedColorDocuments(this.product.colors[0]);
    } else {
      // Priority 2: Fall back to main documents array
      this.selectedGalleryImages = this.getSortedMainDocuments();
    }
  }

  /**
   * Get sorted color documents
   */
  private getSortedColorDocuments(color: any): any[] {
    if (color?.documents && Array.isArray(color.documents)) {
      return [...color.documents].sort((a: any, b: any) => {
        const orderA = a.order !== null && a.order !== undefined ? a.order : 999;
        const orderB = b.order !== null && b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });
    }
    return [];
  }

  /**
   * Get sorted main documents
   */
  private getSortedMainDocuments(): any[] {
    if (this.product?.documents && Array.isArray(this.product.documents)) {
      return [...this.product.documents].sort((a: any, b: any) => {
        const orderA = a.order !== null && a.order !== undefined ? a.order : 999;
        const orderB = b.order !== null && b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });
    }
    return [];
  }

  /**
   * Check if product is from lightweight cache (missing full details)
   */
  private isLightweightCachedProduct(product: any): boolean {
    if (!product) return false;

    // Lightweight cache only has: id, label, code, priceHT, new_price, thumbnail, category
    // Check if missing full product fields
    const hasFullDetails = product.documents || product.colors || product.variants ||
      product.specifications || product.description;

    return !hasFullDetails && product.id;
  }

  /**
   * Load full product details from API
   */
  private loadFullProductDetails(productId: string | number): void {
    this.isLoadingFullDetails = true;

    this.storageService.getProduct(productId.toString()).subscribe({
      next: (response) => {
        // Extract the actual product data from the response structure
        const productData = response?.data?.[0];

        if (productData) {
          // Merge full product data with existing product
          this.product = { ...this.product, ...productData };
          this.initializeProduct();
          this.hasLoadedFullDetails = true;
        } else {
          this.hasLoadedFullDetails = false;
          console.error('Product data not found in response');
        }
        this.isLoadingFullDetails = false;
        if (this.pendingCartAdd) {
          this.pendingCartAdd = false;
          if (productData) {
            this.continueAddToCart();
          } else {
            this.toastr.error('Nie udało się załadować opcji produktu. Spróbuj ponownie.', 'Szczegóły produktu niedostępne');
          }
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading full product details:', error);
        this.isLoadingFullDetails = false;
        // Still initialize with available data
        this.initializeProduct();
        if (this.pendingCartAdd) {
          this.pendingCartAdd = false;
          this.toastr.error('Nie udało się załadować opcji produktu. Spróbuj ponownie.', 'Szczegóły produktu niedostępne');
        }
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Initialize product data (used after loading full details or with complete product)
   */
  private initializeProduct(): void {
    // Use smart image selection logic for gallery images
    this.initializeGalleryImages();
    this.currentImageIndex = 0; // Reset image index when product changes

    // Parse specifications
    this.parseSpecifications();

    // Set initial selected color
    if (this.product?.colors?.length) {
      this.selectedColor = this.product.colors[0];
    }

    // Enhanced unit calculation with comprehensive fallbacks
    const unitSales = this.product?.unit_sales;
    const unitProduct = this.product?.unit_product;

    // Priority: unit_sales > unit_product > default 1
    let primaryUnit = unitSales || unitProduct;

    // If no unit found, create default unit with more specific label
    if (!primaryUnit) {
      primaryUnit = {
        code: '1',
        label: this.determineBestUnitLabel()
      };
    }

    const minimumQuantity = Number(primaryUnit.minimal_quantity);
    this.saleUnit = Number.isFinite(minimumQuantity) && minimumQuantity > 0
      ? minimumQuantity
      : 1;
    // Ensure quantity starts at least at the sale unit
    this.quantity = Math.max(this.saleUnit, 1);

    // Reset variants
    this.variants = {};
    this.selectedVariants = {};

    // Set variants if they exist
    if (this.product?.variants && this.product.variants.length > 0) {
      this.setVariants(this.product.variants);
    }

    this.calculateTotalPrice();
    this.cdr.markForCheck();
  }
}